// Aperçu de la base pour l'invité (spec § 3) : un seul motif partout où il bute sur une limite. Un cadenas, une phrase
// avec le nombre réel, un lien « Voir les offres » ; jamais de fenêtre, de flou ni de faux contenu. Au plus un par écran.
import { useQuery } from "@tanstack/react-query";
import { Lock } from "lucide-react";
import { useListContext, useLocaleState, useTranslate } from "ra-core";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { cn } from "@/lib/utils";

import { getSupabaseClient } from "../providers/supabase/supabase";
import { OfferPage, useOfferOpen } from "./OfferPage";
import { teaserFilters } from "./teaserFilters";
import { useInvited } from "./useInvited";

export const LockNote = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  const translate = useTranslate();
  const offerOpen = useOfferOpen();
  return (
    <p className={cn("text-sm text-muted-foreground", className)}>
      {/* Cadenas, phrase et lien dans le même fil de texte : sur téléphone, le cadenas ne reste pas seul sur sa ligne. */}
      <Lock
        className="mr-1.5 inline size-3.5 -translate-y-px align-middle"
        aria-hidden
      />
      {children}{" "}
      {offerOpen && (
        <Link
          to={OfferPage.path}
          className="ml-1 whitespace-nowrap font-medium text-foreground underline underline-offset-2 hover:text-primary"
        >
          {translate("resources.projects.teaser.offers")}
        </Link>
      )}
    </p>
  );
};

export type BaseCount = {
  grp: string | null;
  projects: number;
  hidden: number;
  hidden_high: number;
};

/** Comptes sur toute la base pour ces filtres (fonction `base_count_projects`) ; rien quand ils sont intraduisibles. */
export const useBaseCount = (
  filters: Record<string, unknown> | null,
  groupBy: string | null = null,
  enabled = true,
) =>
  useQuery({
    queryKey: ["base_count_projects", filters, groupBy],
    enabled: enabled && filters != null,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<BaseCount[]> => {
      const { data, error } = await getSupabaseClient().rpc(
        "base_count_projects",
        { filters, group_by: groupBy },
      );
      if (error) throw error;
      return data ?? [];
    },
  });

/** Sous la liste des projets d'un invité : combien de fiches de la base répondent aux mêmes filtres hors de son accès. */
export const HiddenProjectsNote = ({ className }: { className?: string }) => {
  const invited = useInvited();
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const { filterValues } = useListContext();
  const { data } = useBaseCount(
    teaserFilters(filterValues ?? {}),
    null,
    invited,
  );
  const row = data?.[0];
  if (!invited || !row?.hidden) return null;
  return (
    <LockNote className={className}>
      {translate("resources.projects.teaser.list_hidden", {
        smart_count: row.hidden,
        count: row.hidden.toLocaleString(locale),
      })}
      {row.hidden_high > 0 &&
        translate("resources.projects.teaser.list_hidden_high", {
          count: row.hidden_high.toLocaleString(locale),
        })}
    </LockNote>
  );
};
